import { ErrorMessages } from '@/consts/errors';
import {
  sendAiGenScreenActionAnalytics,
  sendAiGenItemLoadAnalytics,
  AiGenScreenAction,
  ScreenType,
} from '@/utils/analytics';

export interface AnalyticsSettings {
  category: string;
  id: string;
}

/** Send analytics for generation start */
export const sendGenerateStartAnalytics = (settings: AnalyticsSettings) => {
  sendAiGenScreenActionAnalytics({
    action: AiGenScreenAction.GENERATE,
    screen_type: ScreenType.GENERATOR,
    settings,
  });
};

/** Send analytics for generation success */
export const sendGenerateSuccessAnalytics = (
  processingTime: number,
  settings: AnalyticsSettings
) => {
  sendAiGenItemLoadAnalytics({
    result: 'success',
    processing_time: processingTime,
    settings,
  });
};

/** Send analytics for generation failure */
export const sendGenerateFailureAnalytics = (
  processingTime: number,
  settings: AnalyticsSettings,
  errorMessage: string
) => {
  sendAiGenItemLoadAnalytics({
    result: 'failure',
    processing_time: processingTime,
    settings: {
      ...settings,
      error_type: 'generation_failed',
      error_message: errorMessage,
    },
  });
};

/**
 * Parse generation errors and return user-friendly message
 */
export const getGenerationErrorMessage = (error: unknown): string => {
  if (!(error instanceof Error)) {
    return ErrorMessages.generationFailed;
  }

  const message = error.message.toLowerCase();

  // Content moderation
  if (message.includes('prohibited content')) {
    return ErrorMessages.prohibitedContent;
  }

  // Timeout
  if (message.includes('timeout') || message.includes('maximum attempts')) {
    return ErrorMessages.generationTimeout;
  }

  // Rate limiting (429)
  if (message.includes('429') || message.includes('rate limit')) {
    return ErrorMessages.rateLimited;
  }

  // Server errors (5xx)
  if (
    message.includes('500') ||
    message.includes('502') ||
    message.includes('503') ||
    message.includes('504') ||
    message.includes('service unavailable')
  ) {
    return ErrorMessages.serverError;
  }

  // Network errors
  if (
    message.includes('network') ||
    message.includes('fetch') ||
    message.includes('connection')
  ) {
    return ErrorMessages.networkError;
  }

  // No result URL
  if (message.includes('no result url')) {
    return ErrorMessages.noResultUrl;
  }

  return ErrorMessages.generationFailed;
};

/**
 * Format timestamp as "Jan 25, 2.30 PM" (using period instead of colon for filename safety)
 */
export const formatTimestamp = (timestamp: number): string => {
  const date = new Date(timestamp);
  const formatted = date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  // Replace colon with period for filename compatibility
  return formatted.replace(':', '.');
};
